import * as React from 'react';
import { UitkSheetPositionTypes } from './types.esm.js';

/* Collection of utilities used by Sheets */
var focusable;
var componentProps;
var sheetElem;
var currentIndex = -1;
var setFocus = function (ref) {
    if (ref && ref.current) {
        ref.current.focus();
    }
};
var setFocusableElems = function (sheetRef) {
    if (sheetRef && sheetRef.current) {
        focusable = sheetRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    }
};
var setElementFocus = function (sheetRef, focusFirstElement, hasModal) {
    setFocusableElems(sheetRef);
    if (focusFirstElement && focusable.length) {
        focusable && focusable[hasModal ? 1 : 0].focus();
    }
    else {
        setFocus(sheetRef);
    }
};
var trapUpperBoundFocus = function () {
    focusable[focusable.length - 2].focus(); // Send focus to last element before boundary
};
var trapLowerBoundFocus = function () {
    focusable[1].focus(); // Send focus to first element after boundary
};
var defineSheetLayout = function (children) {
    var childrenArray = React.Children.toArray(children);
    var sheetLayoutObj = {
        get hasCloseButton() {
            return !!this.closeButtonElem;
        },
        get hasAlignment() {
            var _a, _b, _c;
            return !!this.hasCloseButton && ((_c = (_b = (_a = this.closeButtonElem) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.alignment) !== null && _c !== void 0 ? _c : 'right');
        },
        get layoutProps() {
            return (this.hasCloseButton &&
                {
                    space: 'two',
                    direction: this.hasAlignment === 'top' ? 'column' : 'row-reverse'
                });
        },
        get closeButtonLayoutProps() {
            return (this.hasCloseButton &&
                this.hasAlignment === 'top' &&
                {
                    alignSelf: 'end'
                });
        },
        closeButtonElem: childrenArray.find(function (child) { var _a; return ((_a = child.type) === null || _a === void 0 ? void 0 : _a.displayName) === 'UitkSheetCloseButton'; }),
        footerElem: childrenArray.find(function (child) { var _a; return ((_a = child.type) === null || _a === void 0 ? void 0 : _a.displayName) === 'UitkSheetFooter'; }),
        childrenElem: childrenArray.filter(function (child) {
            var _a, _b;
            if ((_a = child.type) === null || _a === void 0 ? void 0 : _a.displayName) {
                return !['UitkSheetCloseButton', 'UitkSheetFooter'].includes((_b = child.type) === null || _b === void 0 ? void 0 : _b.displayName);
            }
            return true; // This returns all the other elements
        })
    };
    return sheetLayoutObj;
};
var isDescendant = function (parent, child) {
    var _a;
    /**
     * Checks if the clear button was clicked on the body.
     * This was added because there's an edge case while using pwa in which the button is not treated as a descendant of the sheet.
     * Making the sheet close when clicking the clear button.
     */
    var closestButton = child === null || child === void 0 ? void 0 : child.closest('button');
    var isClearButtonFallback = closestButton && (closestButton === null || closestButton === void 0 ? void 0 : closestButton.className.includes('clear'));
    return !!((_a = parent === null || parent === void 0 ? void 0 : parent.current) === null || _a === void 0 ? void 0 : _a.contains(child)) || isClearButtonFallback;
};
var onBodyClick = function (e) {
    var disableBodyClickHandler = componentProps.disableBodyClickHandler, triggerRef = componentProps.triggerRef, onDismiss = componentProps.onDismiss;
    var target = e.target;
    /**
     * Checks if the sheet is within a shadow Dom and if so, sets the target to the composed path rather than the root.
     * This was added because there was an edge case where the sheet is within a shadow dom and would open and dismiss on the same click.
     */
    if (e.target.shadowRoot) {
        target = e.composedPath()[0];
    }
    if ((triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === target ||
        disableBodyClickHandler ||
        isDescendant(sheetElem, target) ||
        isDescendant(triggerRef, target)) {
        return;
    }
    onDismiss && onDismiss();
};
var onKeyDown = function (e) {
    var disableArrowKeyHandler = componentProps.disableArrowKeyHandler, onDismiss = componentProps.onDismiss;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (disableArrowKeyHandler) {
            return;
        }
        var target = document.activeElement;
        // Don't prevent default if the active element is a select, or number input
        if (target instanceof HTMLSelectElement || (target instanceof HTMLInputElement && target.type === 'number')) {
            return;
        }
        e.preventDefault();
    }
    if (e.key !== 'Escape') {
        return;
    }
    onDismiss && onDismiss();
};
var onKeyUp = function (e) {
    var disableArrowKeyHandler = componentProps.disableArrowKeyHandler, onDismiss = componentProps.onDismiss, triggerRef = componentProps.triggerRef;
    var parent = sheetElem;
    var target;
    if (e.shiftKey && e.key === 'Tab') {
        target = document.activeElement;
        if ((triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === e.target) {
            return false;
        }
    }
    if (parent.current) {
        setFocusableElems(sheetElem);
        if (e.key === 'ArrowDown') {
            if (disableArrowKeyHandler) {
                return;
            }
            target = document.activeElement;
            if ((triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === e.target) {
                currentIndex = 0;
                focusable[currentIndex].focus();
                return;
            }
            var elemToFocusOn = focusable[currentIndex + 1];
            if (elemToFocusOn) {
                currentIndex += 1;
                elemToFocusOn.focus();
            }
        }
        else if (e.key === 'ArrowUp') {
            if (disableArrowKeyHandler) {
                return;
            }
            target = document.activeElement;
            if ((triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === e.target) {
                return false;
            }
            var elemToFocusOn = focusable[currentIndex - 1];
            if (elemToFocusOn) {
                currentIndex -= 1;
                elemToFocusOn.focus();
            }
        }
        else if (e.key === 'Tab') {
            target = document.activeElement;
            /**
             * Checks if the component is within a shadow dom and if so, sets the target to the composed path rather than the root.
             * This was added because there was an edge case where the sheet used for the calendar is within a shadow dom and would open and dismiss while using keyboard navigation.
             */
            if (e.target.shadowRoot) {
                target = e.composedPath()[0];
            }
            for (var i = 0; i <= focusable.length; i++) {
                if (target === focusable[i]) {
                    currentIndex = i;
                }
            }
            var isElemDescendant = isDescendant(parent, target);
            if (!isElemDescendant) {
                onDismiss && onDismiss();
            }
        }
    }
};
var attachHandlers = function (props, sheetRef) {
    var body = document.body;
    componentProps = props;
    sheetElem = sheetRef;
    body.addEventListener('click', onBodyClick, { capture: true });
    body.addEventListener('keydown', onKeyDown);
    body.addEventListener('keyup', onKeyUp);
};
var detachHandlers = function () {
    var body = document.body;
    currentIndex = -1; // Resets the currentIndex back to -1 upon closing
    body.removeEventListener('click', onBodyClick, { capture: true });
    body.removeEventListener('keydown', onKeyDown);
    body.removeEventListener('keyup', onKeyUp);
};
var isValidUitkSheetPosition = function (value) {
    return Object.values(UitkSheetPositionTypes).includes(value);
};
/**
 * Add or remove the "has sheet" class name to the body element
 *
 * @param action 'add' | 'remove'
 * @returns target.add(className) || target.remove(className)
 */
var hasSheetClassName = function (action) {
    var className = 'has-uitk-sheet';
    var target = document.body.classList;
    return action === 'add' ? target.add(className) : target.remove(className);
};

export { attachHandlers, defineSheetLayout, detachHandlers, hasSheetClassName, isValidUitkSheetPosition, setElementFocus, setFocus, setFocusableElems, trapLowerBoundFocus, trapUpperBoundFocus };
